import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useOnglets } from './tabs';

/** Au-dela de cette part de l'ecran, lacher change d'onglet. */
const COMMIT_RATIO = 0.32;

/** Duree du rattrapage apres le lacher, en millisecondes. */
export const SETTLE_MS = 260;

/** Un geste de passage d'onglet en cours. */
export interface TabDrag {
  /**
   * Position continue dans la barre : `2` c'est le troisieme onglet en
   * place, `2.4` c'est deux dixiemes de plus vers le quatrieme.
   */
  pos: number;
  /** Ce que le geste s'autorise a parcourir, bornes comprises. */
  range: [number, number];
  /** Position au lacher : le rattrapage part de la, il faut la garder a l'image. */
  from: number;
  /** Doigt leve : la bande rejoint sa place toute seule. */
  settling: boolean;
}

interface TabNav {
  /** Rang de l'onglet courant, ou `-1` hors des onglets. */
  index: number;
  drag: TabDrag | null;
  /**
   * L'adresse ou mene un retour, quand l'application sait la rendre.
   * `null` en haut de la pile, ou si l'on est arrive par un lien direct.
   */
  backPath: string | null;
  /** L'ecran pose derriere pendant un geste de retour, s'il y en a un. */
  backPeek: string | null;
  showBack: (path: string | null) => void;
  /** Ouvre un geste sur la plage donnee. Rend `false` s'il n'y a rien a tirer. */
  begin: (range: [number, number]) => boolean;
  moveTo: (pos: number) => void;
  /** Doigt leve : on commet, ou l'on revient. */
  end: () => void;
}

const Context = createContext<TabNav | null>(null);

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

/**
 * L'etat du passage d'un onglet a l'autre, partage.
 *
 * Deux gestes le pilotent : le balayage sur l'ecran, et le deplacement de
 * la pastille dans la barre. Ils font la meme chose et doivent donc
 * bouger la meme chose — sans quoi la barre et le contenu raconteraient
 * deux histoires differentes pendant le meme mouvement.
 *
 * Tout tient dans une seule position continue. Elle dit ou en est le
 * geste, quels ecrans sont a l'image, et ou se pose la pastille : un
 * balayage se borne au voisin, un doigt pose sur la barre traverse la
 * rangee entiere, et c'est la seule difference entre les deux.
 */
export function TabNavProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  const { chemins } = useOnglets();
  const index = chemins.indexOf(location.pathname);
  const LAST = chemins.length - 1;
  const [drag, setDrag] = useState<TabDrag | null>(null);
  const [backPeek, setBackPeek] = useState<string | null>(null);
  const settleTimer = useRef(0);

  /*
    Ou mene un retour.

    React Router garde l'historique du navigateur mais ne dit pas ce qu'il
    contient : pour poser l'ecran precedent derriere celui qu'on tire, il
    faut le connaitre. On tient donc notre propre pile — empilee a l'aller,
    depilee au retour, inchangee quand une adresse en remplace une autre.
    Une pile plutot que la simple « adresse d'avant » : apres
    A → B → C → retour, l'ecran d'avant est C, mais le retour mene a A.

    Elle se met a jour *pendant* le rendu, et non dans un effet. C'est le
    seul moyen qu'elle soit juste des le rendu ou l'on arrive : un effet
    s'execute apres, et comme une pile rangee dans une `ref` ne declenche
    aucun nouveau rendu, l'adresse de retour restait en retard d'une
    etape. Depuis Parametres, on revenait a l'onglet visite avant Profil
    au lieu de Profil.

    L'entree est reperee par sa cle et non par son adresse : deux etapes
    peuvent porter la meme adresse, et c'est bien de deux etapes qu'il
    s'agit.
  */
  const here = location.pathname + location.search;
  const [trail, setTrail] = useState<string[]>([]);
  const [current, setCurrent] = useState({ key: location.key, path: here });

  if (location.key !== current.key) {
    setCurrent({ key: location.key, path: here });
    // `POP` couvre aussi la marche avant du navigateur — inatteignable sur
    // telephone, et l'application ne l'appelle jamais.
    /*
      Une valeur calculee, pas une fonction de mise a jour : en mode strict
      React execute le corps du composant deux fois, et un `push` relatif
      s'appliquerait alors deux fois. Rejouer la meme valeur ne change
      rien.
    */
    setTrail(
      navigationType === 'PUSH'
        ? [...trail, current.path]
        : navigationType === 'POP'
          ? trail.slice(0, -1)
          : trail,
    );
  }

  /*
    Seules les adresses de l'interieur de l'application se rendent : le
    peek repasse par l'arbre de routes de `/app/*`, qui ne connait ni la
    connexion ni le choix du spot.
  */
  const previous = trail[trail.length - 1];
  /*
    Et jamais vers l'ecran ou l'on se trouve deja.

    Une navigation `replace` change l'adresse sans toucher a la trace :
    ajouter des membres a un groupe ramene a la fiche du groupe, d'ou l'on
    venait, et la trace pointait alors sur cette meme fiche. Le geste de
    retour posait donc l'ecran courant derriere lui-meme — deux bandes,
    une seule cle, et React demontait tout. On sortait sur un ecran vide.

    Sans chemin distinct, il n'y a rien a poser derriere : le geste se
    contente de revenir en arriere.
  */
  const backPath =
    previous?.startsWith('/app/') && previous !== here ? previous : null;

  const begin = useCallback(
    ([from, to]: [number, number]) => {
      if (index === -1) return false;

      // La rangee ne boucle pas : au bord, il n'y a rien a appeler.
      const range: [number, number] = [clamp(from, 0, LAST), clamp(to, 0, LAST)];
      if (range[0] === range[1]) return false;

      setDrag({ pos: index, range, from: index, settling: false });
      return true;
    },
    [index, LAST],
  );

  const moveTo = useCallback((pos: number) => {
    setDrag((current) =>
      current && !current.settling
        ? { ...current, pos: clamp(pos, current.range[0], current.range[1]) }
        : current,
    );
  }, []);

  const end = useCallback(() => {
    setDrag((current) => {
      if (!current || current.settling) return current;

      /*
        Un voisin s'attrape a moins de la moitie : un geste franc ne se
        mesure pas, il se lit a l'intention. Au-dela, on a traverse des
        onglets pour en viser un — c'est le plus proche qui gagne.
      */
      const delta = current.pos - index;
      const target =
        Math.abs(delta) <= 1
          ? Math.abs(delta) > COMMIT_RATIO
            ? index + Math.sign(delta)
            : index
          : Math.round(current.pos);

      /*
        Le rattrapage se joue en CSS, la navigation seulement a la fin :
        changer de route pendant la transition remplacerait l'ecran en
        plein vol.
      */
      window.clearTimeout(settleTimer.current);
      settleTimer.current = window.setTimeout(() => {
        if (target !== index) navigate(chemins[target]);
        setDrag(null);
      }, SETTLE_MS);

      return { ...current, pos: target, from: current.pos, settling: true };
    });
  }, [index, navigate, chemins]);

  const value = useMemo(
    () => ({ index, drag, backPath, backPeek, showBack: setBackPeek, begin, moveTo, end }),
    [index, drag, backPath, backPeek, begin, moveTo, end],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useTabNav(): TabNav {
  const value = useContext(Context);
  if (!value) throw new Error('useTabNav hors de TabNavProvider');
  return value;
}
